<template>
  <div>
    <!-- Session timed out dialog is page overlay and persistent -->
    <v-dialog
      v-if="showCountdown && hasTimedOut"
      value="true"
      :persistent="true"
      max-width="95%"
      :content-class="sessionTimeoutDialogCssClass"
    >
      <Alert
        type="error"
        :inPage="true"
        icon="alarm"
        :allowDismiss="false"
        :actionText="alertActionText"
        :actionHandler="onClick"
      >
        <template slot="optional">
          <span
            v-html="sessionExpiredText"
            role="alert"
          />
        </template>
      </Alert>
    </v-dialog>

    <!-- Session countdown alert is in page and not persistent -->
    <Alert
      v-if="showCountdown && !hasTimedOut"
      type="warning"
      :inPage="true"
      icon="alarm"
      :allowDismiss="true"
      :actionText="alertActionText"
      :actionHandler="onClick"
      :class="sessionCountdownAlertCssClass"
    >
      <template slot="optional">
        <span
          aria-hidden="true"
          v-html="sessionCountdownText"
        />
        <span
          class="d-sr-only"
          v-html="sessionCountdownTextForScreenReader"
          role="alert"
        />
      </template>
    </Alert>
  </div>
</template>

<script>
import { Alert } from '@nswdoe/doe-ui-core'

export default {
  name: 'SessionCountdown',
  components: { Alert },
  props: {
    showCountdown: {
      type: Boolean,
      default: false
    },
    minutesWarning: {
      type: Number,
      default: 5
    },
    sessionCountdownAlertCssClass: {
      type: String,
      default: null
    },
    sessionTimeoutDialogCssClass: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      countdownTimer: null,
      timeout: {
        mins: this.minutesWarning,
        secs: 0
      },
    }
  },
  watch: {
    showCountdown: {
      handler(newValue) {
        //If we have just started displaying the banner, then start the countdown and event listeners
        if (newValue) {
          this.addListeners()
          this.beginCountdown()
        }
      },
      immediate: true
    },
    hasTimedOut(value) {
      if (value) {
        this.$emit('expired')
      }
    }
  },
  computed: {
    hasTimedOut() {
      return this.timeout.mins === 0 && this.timeout.secs === 0
    },
    alertActionText() {
      return this.hasTimedOut ? 'LOGIN TO CONTINUE' : 'CONTINUE SESSION'
    },
    sessionExpiredText() {
      return '<strong>Your session has expired. Please log in again to continue.</strong>'
    },
    sessionCountdownText() {
      return `Your session will expire in <strong>${this.timeout.mins} minute${this.timeout.mins !== 1 ? 's' : ''}, ${this.timeout.secs} second${this.timeout.secs !== 1 ? 's' : ''}</strong>`
    },
    sessionCountdownTextForScreenReader() {
      return `Your session will expire in about ${this.timeout.mins} minute${this.timeout.mins !== 1 ? 's' : ''}`
    }
  },
  methods: {
    beginCountdown() {
      this.timeout.mins = this.minutesWarning
      this.timeout.secs = 0
      this.countdownTimer = setInterval(this.countDownOneSecond, 1000)
    },
    addListeners() {
      window.addEventListener('keydown', this.continueSession)
      window.addEventListener('mousedown', this.continueSession)
    },
    removeListeners() {
      window.removeEventListener('keydown', this.continueSession)
      window.removeEventListener('mousedown', this.continueSession)
    },
    countDownOneSecond() {
      let mins = this.timeout.mins
      let secs = this.timeout.secs
      if (mins === 0 && secs === 0) {
        clearInterval(this.countdownTimer)
        this.removeListeners()
      } else if (secs === 0) {
        this.timeout.mins = mins - 1
        this.timeout.secs = 59
      } else {
        this.timeout.secs = secs - 1
      }
    },
    continueSession() {
      clearInterval(this.countdownTimer)
      this.removeListeners()
      this.$activityMonitor.activate()
      this.$emit('continue')
    },
    onClick() {
      this.hasTimedOut ? this.$emit('login') : this.continueSession()
    },
  },
  beforeDestroy() {
    this.removeListeners()
    clearInterval(this.countdownTimer)
  }
}
</script>

<style scoped lang="scss">
::v-deep .v-btn {
  border: none;
}
::v-deep .v-dialog {
  box-shadow: none;
  position: absolute;
}
</style>
